/**
 * Remembering which creator sent someone.
 *
 * Two ways in, because the creator promotes both: a link with `?ref=CODE`, and a code read
 * out in a caption or comment and typed by hand. TikTok viewers frequently never tap the bio
 * link, so the typed path is not a fallback — it may be the common one.
 *
 * The code has to survive a lot between arriving and the account existing: navigation around
 * the marketing pages, the signup form, and for Google sign-in a full redirect off-site and
 * back. localStorage is the only thing that survives all of that.
 */

const KEY = 'autoinvent.referral';
/** Matches the attribution window on the server — a code older than this is not worth keeping. */
const TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Codes get retyped from video captions, so treat them loosely. */
export function normalizeCode(raw) {
  return String(raw || '').trim().toUpperCase().replace(/\s+/g, '');
}

/**
 * Record a code seen in the URL. First one wins for the life of the stored value: if someone
 * arrives through one creator and later clicks another's link before signing up, the creator
 * who actually introduced them keeps the credit.
 */
export function captureReferralFromUrl(search = window.location.search) {
  try {
    const code = normalizeCode(new URLSearchParams(search).get('ref'));
    if (!code) return null;
    const existing = readReferral();
    if (existing) return existing;
    localStorage.setItem(KEY, JSON.stringify({ code, seenAt: Date.now() }));
    return code;
  } catch {
    // Private browsing, or storage disabled. The typed field still works.
    return null;
  }
}

/** The stored code, or null once it is older than the attribution window. */
export function readReferral() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { code, seenAt } = JSON.parse(raw);
    if (!code || !seenAt || Date.now() - seenAt > TTL_MS) {
      localStorage.removeItem(KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

export function clearReferral() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
